import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { useJurisdictions } from '@dealstack/frontend/shared/jurisdictions.ts';
import { useActiveLegalForms } from '@dealstack/frontend/shared/legalForms.ts';
import { isPersonLegal } from '@dealstack/shared/lib/common/business/person.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { updatePersonLegal } from '@dealstack/frontend/app/services/Person.service.ts';
import {
	REGISTRATION_DETAILS_FORM_FIELDS,
	RegistrationDetailsFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/types.ts';
import { RegistrationDetailsFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/LegalPersonDetails/hook/util/registrationDetailsShape.ts';

export const useRegistrationDetailsSection = () => {
	const jurisdictions = useJurisdictions();
	const activeLegalForms = useActiveLegalForms(jurisdictions);

	const {
		selectedEntityId,
		selectedEntityData,
		refetchSelectedEntity,
		isFetchingSelectedEntityData,
		refetchPersonsAndTags,
	} = useContext(CapManageControllerContext);

	const updateLegalPersonDetailsRequest = useMutation({
		mutationKey: ['updatePersonLegal', selectedEntityId],
		mutationFn: updatePersonLegal,
	});

	const registrationDetailsForm = useForm<RegistrationDetailsFormValues>({
		mode: 'all',
		resolver: zodResolver(RegistrationDetailsFormShape),
	});

	const formValues = registrationDetailsForm.watch();
	const jurisdictionNameValue = registrationDetailsForm.watch(
		REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_name
	);

	useEffect(() => {
		if (!selectedEntityData || !isPersonLegal(selectedEntityData.type)) {
			return;
		}

		const { registrationDetails } = selectedEntityData;

		registrationDetailsForm.reset({
			[REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_name]:
				registrationDetails?.jurisdiction?.jurisdiction_name,
			[REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_language]:
				registrationDetails?.selectedJurisdictionLanguage,
			[REGISTRATION_DETAILS_FORM_FIELDS.legal_form_id]:
				registrationDetails?.legalForm?.uniq_id_by_elf_code_and_language,
			[REGISTRATION_DETAILS_FORM_FIELDS.registration_number]:
				registrationDetails?.corporateRegistrationNumber,
			[REGISTRATION_DETAILS_FORM_FIELDS.registered_name]: registrationDetails?.name,
		});
	}, [selectedEntityData]);

	const onFormSubmit = async (formValues: RegistrationDetailsFormValues) => {
		const {
			jurisdiction_name,
			jurisdiction_language,
			legal_form_id,
			registration_number,
			registered_name,
		} = formValues;

		return updateLegalPersonDetailsRequest.mutateAsync(
			{
				personId: selectedEntityId,
				updateArgs: {
					registrationDetails: {
						jurisdictionName: jurisdiction_name || '',
						selectedJurisdictionLanguage: jurisdiction_language || '',
						legalFormId: legal_form_id || '',
						corporateRegistrationNumber: registration_number || '',
						name: registered_name || '',
					},
				},
			},
			{
				onSuccess: () => {
					void refetchSelectedEntity();
					void refetchPersonsAndTags();
				},
			}
		);
	};

	const { jurisdictionLanguages, showJurisdictionLanguageSelect } = useMemo((): {
		jurisdictionLanguages?: string[];
		showJurisdictionLanguageSelect: boolean;
	} => {
		const jurisdictionName = registrationDetailsForm.getValues(
			REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_name
		);

		const jurisdictionData = jurisdictions.find(
			(j) => j.jurisdiction_name === jurisdictionName
		);

		return {
			jurisdictionLanguages: jurisdictionData?.languages,
			showJurisdictionLanguageSelect: jurisdictionData?.languages
				? jurisdictionData?.languages.length > 1
				: false,
		};
	}, [jurisdictions, jurisdictionNameValue]);

	const jurisdictionName = useWatch({
		control: registrationDetailsForm.control,
		name: REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_name,
	});

	const jurisdictionLanguage = useWatch({
		control: registrationDetailsForm.control,
		name: REGISTRATION_DETAILS_FORM_FIELDS.jurisdiction_language,
	});

	const legalFormOptions = useMemo((): { value: string; label: string }[] => {
		if (!jurisdictionName) {
			return [];
		}

		if (!showJurisdictionLanguageSelect) {
			return activeLegalForms.findForJurisdictionName(jurisdictionName);
		}

		if (!jurisdictionLanguage) {
			return [];
		}

		return activeLegalForms.findForJurisdictionName(jurisdictionName, jurisdictionLanguage);
	}, [showJurisdictionLanguageSelect, activeLegalForms, jurisdictionName, jurisdictionLanguage]);

	// Mantine v7 Combobox groups via nested `{ group, items }` entries (the v6
	// flat `{ value, label, group }` shape makes v7 read `item.items` and crash)
	// and rejects a `value` repeated across the whole option set. The source data
	// lists common jurisdictions in both a featured group and their region, so to
	// keep the v6 behaviour of showing them in both places each option gets a
	// unique `group::name` value. `jurisdictionNameByValue` maps that back to the
	// bare jurisdiction name the form stores; `jurisdictionValueByName` resolves a
	// stored name to the first matching option so the field renders selected.
	const { jurisdictionsOptions, jurisdictionNameByValue, jurisdictionValueByName } =
		useMemo(() => {
			const groups = new Map<string, { value: string; label: string }[]>();
			const ungrouped: { value: string; label: string }[] = [];
			const nameByValue: Record<string, string> = {};
			const valueByName: Record<string, string> = {};
			jurisdictions.forEach((jurisdiction) => {
				const name = jurisdiction.jurisdiction_name;
				const value = `${jurisdiction.group}::${name}`;
				const option = { value, label: name };
				nameByValue[value] = name;
				if (valueByName[name] === undefined) {
					valueByName[name] = value;
				}
				if (jurisdiction.group) {
					groups.set(jurisdiction.group, [
						...(groups.get(jurisdiction.group) ?? []),
						option,
					]);
				} else {
					ungrouped.push(option);
				}
			});
			return {
				jurisdictionsOptions: [
					...Array.from(groups, ([group, items]) => ({ group, items })),
					...ungrouped,
				],
				jurisdictionNameByValue: nameByValue,
				jurisdictionValueByName: valueByName,
			};
		}, [jurisdictions]);

	return {
		selectedEntityData,
		registrationDetailsForm,
		onFormSubmit,
		isLoading: isFetchingSelectedEntityData || updateLegalPersonDetailsRequest.isPending,
		showJurisdictionLanguageSelect,
		jurisdictionLanguages,
		legalFormOptions,
		refetchPersonsAndTags,
		jurisdictionsOptions,
		jurisdictionNameByValue,
		jurisdictionValueByName,
		formValues,
	};
};
